import { useEffect, useMemo, useState } from "react";
import {
  Download,
  FileSignature,
  Loader2,
  Paperclip,
  Trash2,
  UploadCloud,
} from "lucide-react";
import type { ContractRecord, AdminPanelUser } from "../types/admin-panel";
import {
  deleteContract,
  downloadContract,
  storeContract,
} from "../api/admin-panel";

type Props = {
  users: AdminPanelUser[];
  contracts: ContractRecord[];
  loading?: boolean;
  onReload?: () => Promise<void> | void;
};

export function ContractsPanel({
  users,
  contracts,
  loading = false,
  onReload,
}: Props) {
  const [usuarioId, setUsuarioId] = useState("");
  const [archivos, setArchivos] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [page, setPage] = useState(1);

  const ITEMS_PER_PAGE = 4;

  const empleados = useMemo(
    () => users.sort((a, b) => a.nombre.localeCompare(b.nombre, "es")),
    [users],
  );

  const selectedUser =
    usuarioId === "all"
      ? null
      : empleados.find((u) => String(u.id) === usuarioId);

  const filteredContracts = useMemo(() => {
    return contracts.filter((item) => {
      const createdAt = new Date(item.fecha_subida);

      const matchesSearch =
        !search || item.empleado.toLowerCase().includes(search.toLowerCase());

      const matchesMonth =
        !filterMonth || String(createdAt.getMonth() + 1) === filterMonth;

      const matchesYear =
        !filterYear || String(createdAt.getFullYear()) === filterYear;

      return matchesSearch && matchesMonth && matchesYear;
    });
  }, [contracts, search, filterMonth, filterYear]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredContracts.length / ITEMS_PER_PAGE),
  );

  const paginatedContracts = filteredContracts.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  useEffect(() => {
    setPage(1);
  }, [search, filterMonth, filterYear]);

  const onSubmit = async () => {
    if (!usuarioId || archivos.length === 0) return;

    setSaving(true);

    try {
      await storeContract(
        usuarioId === "all" ? "all" : Number(usuarioId),
        archivos,
      );
      setUsuarioId("");
      setArchivos([]);
      await onReload?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="scroll-reveal-left space-y-5">
      <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="dashboard-scale-in group relative overflow-hidden rounded-[32px] border border-[#e7ecef] bg-white/90 shadow-[0_14px_45px_rgba(15,23,42,.055)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-emerald-100 hover:shadow-[0_24px_70px_rgba(15,23,42,.08)]">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -right-24 top-[-90px] h-[260px] w-[260px] rounded-full bg-emerald-300/22 blur-3xl transition duration-500 group-hover:scale-110" />
            <div className="absolute bottom-[-120px] left-[-70px] h-[240px] w-[240px] rounded-full bg-teal-300/14 blur-3xl transition duration-500 group-hover:scale-110" />
          </div>

          <div className="relative flex flex-col gap-4 border-b border-[#edf1f3] bg-white/65 px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1.5 text-xs font-black uppercase tracking-[0.16em] text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.07)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-emerald-100">
                <FileSignature size={14} />
                Archivo laboral
              </div>

              <h3 className="mt-3 text-2xl font-black tracking-tight text-slate-950">
                Administrar contratos
              </h3>

              <p className="mt-1 max-w-2xl text-sm font-semibold leading-6 text-slate-500">
                Adjunta documentación contractual, anexos, firmas y archivos
                vinculados al expediente del empleado.
              </p>
            </div>

            <div className="rounded-[28px] border border-emerald-100 bg-white/80 px-5 py-4 shadow-[0_8px_22px_rgba(16,185,129,.06)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-0.5 hover:bg-emerald-50">
              <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                Documentos
              </div>

              <div className="mt-1 text-3xl font-black text-slate-950">
                {contracts.length}
              </div>
            </div>
          </div>

          <div className="relative grid gap-4 p-6 lg:grid-cols-[320px_minmax(0,1fr)]">
            <Field label="Empleado">
              <select
                value={usuarioId}
                onChange={(e) => setUsuarioId(e.target.value)}
                className="field-control"
                disabled={loading || saving}
              >
                <option value="">Selecciona usuario</option>
                <option value="all">Todos los usuarios</option>

                {empleados.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nombre}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Archivos">
              <label className="group/file flex h-[52px] cursor-pointer items-center justify-center gap-2 rounded-[20px] border border-dashed border-emerald-200 bg-emerald-50/70 px-4 text-sm font-black text-emerald-700 shadow-[0_8px_22px_rgba(16,185,129,.06)] transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-300 hover:bg-emerald-100/80 hover:shadow-[0_14px_32px_rgba(16,185,129,.12)]">
                <Paperclip
                  size={17}
                  className="transition duration-300 group-hover/file:-rotate-6 group-hover/file:scale-110"
                />
                Seleccionar archivo(s)
                <input
                  key={archivos.length === 0 ? "empty" : "selected"}
                  onChange={(e) =>
                    setArchivos(Array.from(e.target.files ?? []))
                  }
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                  disabled={saving}
                  className="hidden"
                />
              </label>
            </Field>
          </div>

          {archivos.length > 0 && (
            <div className="relative px-6 pb-6">
              <div className="animate-slide-up rounded-[26px] border border-emerald-100 bg-emerald-50/80 p-4 text-sm font-bold text-slate-600 shadow-[0_10px_28px_rgba(16,185,129,.08)]">
                <div className="font-black text-emerald-700">
                  {archivos.length} archivo(s) preparado(s) para subir
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {archivos.map((file) => (
                    <span
                      key={`${file.name}-${file.size}`}
                      className="rounded-full border border-emerald-100 bg-white px-3 py-1.5 text-xs font-black text-slate-600 shadow-[0_6px_16px_rgba(15,23,42,.04)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-emerald-50 hover:text-emerald-700"
                    >
                      {file.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="relative flex flex-col gap-3 border-t border-[#edf1f3] bg-slate-50/70 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm font-semibold text-slate-500">
              {usuarioId === "all"
                ? "Se guardará una copia en el expediente de todos los usuarios."
                : selectedUser
                  ? `Se guardará en el expediente de ${selectedUser.nombre}.`
                  : "Selecciona un usuario y añade al menos un archivo."}
            </p>

            <button
              onClick={onSubmit}
              disabled={saving || !usuarioId || archivos.length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#10b981,#14b8a6)] px-5 py-3 text-sm font-black text-white shadow-[0_14px_32px_rgba(16,185,129,.20)] transition-all duration-300 hover:-translate-y-1 hover:scale-[1.01] hover:shadow-[0_20px_42px_rgba(16,185,129,.26)] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:scale-100"
            >
              {saving ? (
                <Loader2 size={17} className="animate-spin" />
              ) : (
                <UploadCloud size={17} />
              )}
              Subir contrato(s)
            </button>
          </div>
        </div>

        <aside className="dashboard-slide-right group rounded-[32px] border border-[#e7ecef] bg-white/90 p-5 shadow-[0_14px_45px_rgba(15,23,42,.045)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-emerald-100 hover:shadow-[0_24px_60px_rgba(15,23,42,.07)]">
          <div className="relative overflow-hidden rounded-[28px] border border-emerald-100 bg-[linear-gradient(135deg,#ffffff,#f0fdf4)] p-5 text-slate-950 shadow-[0_10px_28px_rgba(16,185,129,.08)]">
            <div className="pointer-events-none absolute -right-16 -top-16 h-36 w-36 rounded-full bg-emerald-300/24 blur-3xl transition duration-500 group-hover:scale-110" />

            <div className="relative grid h-12 w-12 place-items-center rounded-2xl border border-emerald-100 bg-white text-emerald-700 shadow-[0_10px_24px_rgba(16,185,129,.10)] transition-all duration-300 group-hover:-rotate-3 group-hover:scale-105">
              <FileSignature size={20} />
            </div>

            <h4 className="relative mt-5 text-xl font-black tracking-tight text-slate-950">
              Expediente limpio
            </h4>

            <p className="relative mt-2 text-sm font-semibold leading-6 text-slate-500">
              Mantén contratos y anexos centralizados para acelerar consultas de
              RRHH y auditorías internas.
            </p>
          </div>
        </aside>
      </section>

      <section className="dashboard-scale-in overflow-hidden rounded-[32px] border border-[#e7ecef] bg-white/90 shadow-[0_14px_45px_rgba(15,23,42,.055)] backdrop-blur-xl transition-all duration-300 hover:border-emerald-100 hover:shadow-[0_22px_60px_rgba(15,23,42,.075)]">
        <div className="border-b border-[#edf1f3] px-5 py-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <h3 className="text-lg font-black text-slate-950">
                Contratos recientes
              </h3>

              <p className="text-sm font-semibold text-slate-500">
                Consulta, descarga o elimina documentos asociados.
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar empleado..."
                className="field-control min-w-[220px]"
              />

              <select
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="field-control"
              >
                <option value="">Todos los meses</option>
                {months.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>

              <input
                type="number"
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                placeholder="Año"
                className="field-control w-[120px]"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-5">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="mb-3 h-12 animate-pulse rounded-2xl bg-slate-100"
              />
            ))}
          </div>
        ) : filteredContracts.length === 0 ? (
          <div className="grid min-h-[220px] place-items-center p-8 text-center">
            <div>
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700 shadow-[0_10px_24px_rgba(16,185,129,.08)]">
                <FileSignature size={22} />
              </div>

              <p className="mt-4 text-sm font-black text-slate-950">
                No hay contratos para los filtros seleccionados.
              </p>

              <p className="mt-1 text-sm font-semibold text-slate-500">
                Ajusta los filtros o sube nuevos documentos.
              </p>
            </div>
          </div>
        ) : (
          <div className="max-h-[620px] overflow-auto">
            <table className="table-modern table-dark min-w-[860px]">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th>Empleado</th>
                  <th>Archivo</th>
                  <th>Fecha</th>
                  <th className="text-right">Acciones</th>
                </tr>
              </thead>

              <tbody>
                {paginatedContracts.map((c) => (
                  <tr key={c.id} className="group">
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="grid h-10 w-10 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-sm font-black text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.06)] transition-all duration-300 group-hover:-rotate-3 group-hover:scale-105">
                          {getInitials(c.empleado)}
                        </div>

                        <span className="font-black text-slate-950">
                          {c.empleado}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="max-w-[360px] truncate font-semibold text-slate-500">
                        {c.nombre_archivo}
                      </div>
                    </td>

                    <td>{formatDate(c.fecha_subida)}</td>

                    <td>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => downloadContract(c.id)}
                          className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-[0_8px_20px_rgba(15,23,42,.04)] transition-all duration-300 hover:-translate-y-1 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 hover:shadow-[0_12px_28px_rgba(16,185,129,.10)]"
                          title="Ver / descargar"
                        >
                          <Download size={15} />
                        </button>

                        <button
                          type="button"
                          onClick={async () => {
                            if (!confirm("¿Eliminar este contrato?")) return;

                            await deleteContract(c.id);
                            await onReload?.();
                          }}
                          className="grid h-9 w-9 place-items-center rounded-full border border-rose-100 bg-rose-50 text-rose-500 shadow-[0_8px_20px_rgba(244,63,94,.06)] transition-all duration-300 hover:-translate-y-1 hover:bg-rose-100 hover:text-rose-600 hover:shadow-[0_12px_28px_rgba(244,63,94,.12)]"
                          title="Eliminar"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-between border-t border-[#edf1f3] px-5 py-4">
          <div className="text-sm font-bold text-slate-500">
            Página {page} de {totalPages}
          </div>

          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:opacity-40"
            >
              Anterior
            </button>

            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:opacity-40"
            >
              Siguiente
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-black uppercase tracking-[0.14em] text-slate-400">
        {label}
      </span>

      {children}
    </label>
  );
}

function getInitials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function formatDate(value: string) {
  return new Date(value).toLocaleString("es-ES");
}

const months = [
  { value: "1", label: "Enero" },
  { value: "2", label: "Febrero" },
  { value: "3", label: "Marzo" },
  { value: "4", label: "Abril" },
  { value: "5", label: "Mayo" },
  { value: "6", label: "Junio" },
  { value: "7", label: "Julio" },
  { value: "8", label: "Agosto" },
  { value: "9", label: "Septiembre" },
  { value: "10", label: "Octubre" },
  { value: "11", label: "Noviembre" },
  { value: "12", label: "Diciembre" },
];